import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ApolloProvider } from '@apollo/client';
import DashboardLayout from "@/components/DashboardLayout";
import Explore from "@/pages/explore";
import BillboardDetail from "@/pages/billboard-detail";
import Cart from "@/pages/cart";
import Login from "@/pages/login";
import Register from "@/pages/register";
import VerifyEmail from "@/pages/verify-email";
import ForgotPassword from "@/pages/forgot-password";
import Campaigns from "@/pages/campaigns";
import Account from "@/pages/account";
import ContentHub from "@/pages/content-hub";
import ProofOfPlay from "@/pages/proof-of-play";
import DeliveryReport from "@/pages/delivery-report";
import ProofOfLogs from "@/pages/proof-of-logs";
import DailyReport from "@/pages/DailyReport";
import PaymentInvoice from "@/pages/payment-invoice";
import Checkout from "@/pages/checkout";
import CreativeManagement from "@/pages/creative-management";
import StartCampaign from "@/pages/start-campaign";
import CampaignBillboardDetail from "@/pages/campaign-billboard-detail";
import PackageDetail from "@/pages/package-detail";
import DealDetail from "@/pages/deal-detail";
import MediaOwnerStore from "@/pages/media-owner-store";
import MediaOwners from "@/pages/media-owners";
import ContactMediaOwner from "@/pages/contact-media-owner";
import CampaignReview from "@/pages/campaign-review";
import NotFound from "@/pages/not-found";
import { AuthProvider } from './utils/authContext';
// import { NotificationProvider } from "@/contexts/NotificationContext";
import "react-toastify/dist/ReactToastify.css";
// import "@/App.css";
import type { Configuration } from "./types/config";
import { Get_Country_Code, Get_Currency_Code_Details } from "./Graphql/Queries";
import { generateMockData } from "./Graphql/MockData";
import client from "./Graphql/apolloClient";
import { normalizeDefault } from "./utils/validationUtils";
import { CartProvider } from "./utils/cartContext";
import { Toaster } from "./components/ui/toaster";
import Landing from "./pages/landing";
import ResetPassword from "./pages/reset-password";

// Helper function to check if login is enabled
const checkLoginEnabled = (config: any[]): boolean => {
  const landingPageConfig = config?.find((page: { name: string }) => page.name === "Landing Page");
  const configdata = landingPageConfig?.configurations || [];
  const loginConfig = configdata?.find((field: any) => field.key === "login");
  return loginConfig ? loginConfig.default : true;
};

// Helper function to check if explore is enabled
const checkExploreEnabled = (config: any[]): boolean => {
  const landingPageConfig = config?.find((page: { name: string }) => page.name === "Landing Page");
  const configdata = landingPageConfig?.configurations || [];
  const exploreConfig = configdata?.find((field: any) => field.key === "Explore");
  return exploreConfig ? exploreConfig.default : true;
};

function App() {
  const base_path = import.meta.env.VITE_APP_BASE_PATH || '';
  const exposure = import.meta.env.VITE_APP_EXPOSURE || '';
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [isLandingPage, setIsLandingPage] = useState(true);
  const [configData, setConfigData] = useState<any[]>([]);
  const [currencyCode, setCurrencyCode] = useState<string>("");
  const [countryId, setCountryId] = useState<string>("");
  const [isExploreEnabled, setIsExploreEnabled] = useState(true);
  const [isLoginEnabled, setIsLoginEnabled] = useState(true);
  const [yourLogo, setyourLogo] = useState<string>('');
  const [lightLogo, setLightLogo] = useState<string>('');
  const [registrationFields, setRegistrationFields] = useState<{ key: string; default: boolean }[]>([]);

  const [countryName, setCountryName] = useState('');
  const [tax, setTax] = useState<{ label: string; percent: number } | null>(null);

  // Protected route component for login-related pages
  const ProtectedLoginRoute = ({ children }: { children: React.ReactNode }) => {
    if (!checkLoginEnabled(configData)) {
      // Redirect to landing page if login is disabled
      return <Navigate to={`${base_path}/`} replace />;
    }
    return <>{children}</>;
  };

  // Protected route component for explore page
  const ProtectedExploreRoute = ({ children }: { children: React.ReactNode }) => {
    if (!checkExploreEnabled(configData)) {
      // Redirect to landing page if explore is disabled
      return <Navigate to={`${base_path}/`} replace />;
    }
    return <>{children}</>;
  };


  // Fetch country code
  const getCountryCode = async (companyId: string) => {
    try {
      const { data } = await client.query({
        query: Get_Country_Code,
        variables: { ownerId: companyId }
      });
      if (data && data?.getCountryCodeNoToken) {
        localStorage.setItem("countryId", data?.getCountryCodeNoToken?.countryId);
        setCountryId(data?.getCountryCodeNoToken?.countryId);
        if (!data?.getCountryCodeNoToken?.countryName) {
          return;
        }
        setCountryName(data?.getCountryCodeNoToken?.countryName);
        localStorage.setItem("countryName", data.getCountryCodeNoToken?.countryName);
        // Set tax data
        if (data?.getCountryCodeNoToken?.tax) {
          setTax({
            label: data.getCountryCodeNoToken.tax.label,
            percent: data.getCountryCodeNoToken.tax.percent
          });
          localStorage.setItem("tax", JSON.stringify(data.getCountryCodeNoToken.tax));
        }
      }
    } catch (error) {
      console.error("Error fetching country details:", error);
    }
  };

  // Fetch currency code
  const fetchCurrencyCode = async (countryName: string) => {
    try {
      if (!countryName) {
        return;
      }

      const variables = {
        countryCurrencyCode: countryName
      };

      const { data } = await client.query({
        query: Get_Currency_Code_Details,
        variables,
      });

      if (data?.currencyCodeDetails?.currencyCode) {
        setCurrencyCode(data.currencyCodeDetails.currencyCode);
        localStorage.setItem("currencyCode", data.currencyCodeDetails.currencyCode);
      }
    } catch (err) {
      console.error("Fetch Currency Code Error:", err);
    }
  };

  useEffect(() => {
    const companyId = import.meta.env.VITE_APP_COMPANY_ID;
    if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
      generateMockData(Get_Country_Code).then((mockData: any) => {
        localStorage.setItem("countryId", mockData.data.getCountryCodeNoToken?.countryId);
        setCountryId(mockData.data?.getCountryCodeNoToken?.countryId);
        setCountryName(mockData.data?.getCountryCodeNoToken?.countryName);
        localStorage.setItem("countryName", mockData.data.getCountryCodeNoToken?.countryName);
        // Set tax data from mock
        if (mockData.data?.getCountryCodeNoToken?.tax) {
          setTax({
            label: mockData.data.getCountryCodeNoToken.tax.label,
            percent: mockData.data.getCountryCodeNoToken.tax.percent
          });
          localStorage.setItem("tax", JSON.stringify(mockData.data.getCountryCodeNoToken.tax));
        }
      });
      generateMockData(Get_Currency_Code_Details).then((mockData: any) => {
        const { currencyCode } = mockData.data.currencyCodeDetails;
        setCurrencyCode(currencyCode);
        localStorage.setItem("currencyCode", currencyCode);
      });
    } else {
      if (!companyId) {
        const storedCompanyId = localStorage.getItem("companyId") || "";
        if (!storedCompanyId) {
          console.error("Company ID is not set in environment variables or local storage.");
          return;
        }
        localStorage.setItem("companyId", storedCompanyId);
        getCountryCode(storedCompanyId);
      } else {
        localStorage.setItem("companyId", companyId);
        getCountryCode(companyId);
      }
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA !== "true" && countryName) {
      fetchCurrencyCode(countryName);
    }
  }, [countryName]);


  // Fetch config and content (adapt as needed)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoadingConfig(true);
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`;
        const configResponse = await fetch(url);
        if (!configResponse.ok) throw new Error("Config fetch failed");
        const configData = await configResponse.json();
        setConfigData(configData);

        // Example: set landing page flag and logo
        const landingPageConfig = configData.find((page: any) => page.name === "Landing Page");
        const landingPageEnabled = landingPageConfig?.configurations?.find((c: any) => c.key === "do_you_need_landing_page")?.default ?? true;
        setIsLandingPage(landingPageEnabled);

        const exploreConfig = landingPageConfig.configurations.find(
          (config: Configuration) => config.key === "Explore"
        );
        const loginConfig = landingPageConfig.configurations.find(
          (config: Configuration) => config.key === "login"
        );
        if (exploreConfig) {

          setIsExploreEnabled(normalizeDefault(exploreConfig.default));
        }
        if (loginConfig) {
          setIsLoginEnabled(normalizeDefault(loginConfig.default));
        }
        if (landingPageConfig) {
          const config = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "do_you_need_landing_page"
          );

          // Set landing page flag based on configuration
          // If config is undefined or not found, default to keeping the landing page visible
          const landingPageEnabled = config ? normalizeDefault(config.default) : true;
          setIsLandingPage(landingPageEnabled);

          const exploreConfig = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "Explore"
          );
          const loginConfig = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "login"
          );
          if (exploreConfig) {

            setIsExploreEnabled(normalizeDefault(exploreConfig.default));
          }
          if (loginConfig) {
            setIsLoginEnabled(normalizeDefault(loginConfig.default));
          }
        }

        const userRegistrationConfig = configData.find((page: any) => page.name === "User Registration");

        if (userRegistrationConfig && userRegistrationConfig.configurations) {
          const basicInfoConfig = userRegistrationConfig.configurations.find(
            (c: any) => c.key === "basicInformation"
          );
          if (basicInfoConfig && basicInfoConfig.fields) {
            setRegistrationFields(basicInfoConfig.fields);
          } else {
            console.error("Basic Information config not found in User Registration");
            setRegistrationFields([]);
          }
        } else {
          console.error("User Registration config not found");
          setRegistrationFields([]);
        }
      } catch (error) {
        setIsLandingPage(true);
        setRegistrationFields([]);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;

        const response = await fetch(url);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error("Non-JSON response:", text);
          throw new Error("Response is not JSON");
        }

        const data = await response.json();
        // setContentData(data);

        const pageData = data.find((page: any) => page.name === "Landing Page");
        if (!pageData) {
          console.warn("Landing Page data not found");
          return;
        }

        const mediumLogoData = pageData.fields[0]?.components[0]?.components[1]?.defaultValue[0]?.MediumLogo;
        if (mediumLogoData?.length > 0) {
          const logo = mediumLogoData[0];
          const imgUrl = logo.url
            ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${logo.url}`
            : logo.data?.url
              ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${logo.data.url}`
              : null;
          if (imgUrl) setyourLogo(imgUrl);
        }

        const lightLogoData = pageData.fields[0]?.components[0]?.components[3]?.defaultValue[0]?.LightLogo;
        if (lightLogoData?.length > 0) {
          const logo = lightLogoData[0];
          const imgUrl = logo.url
            ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${logo.url}`
            : logo.data?.url
              ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${logo.data.url}`
              : null;
          if (imgUrl) setLightLogo(imgUrl);
          console.log("Light logo URL set to:", imgUrl);
        }

        const faviconComponentList = pageData.fields[0]?.components[0]?.components;
        const faviconGrid = faviconComponentList?.find((item: any) => item.key === "faviconGrid");
        if (!faviconGrid?.defaultValue?.length) return;

        let fullFaviconUrl = `${import.meta.env.VITE_APP_FOLDER_PATH}/favicon.ico`;
        const faviconDisplay = faviconGrid.defaultValue[0]?.favIconDisplay; // Use favIconDisplay instead of faviconDisplay

        if (faviconDisplay?.length > 0) {

          const faviconItem = faviconDisplay[0];
          fullFaviconUrl = faviconItem.url
            ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${faviconItem.url}`
            : faviconItem.data?.url
              ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${faviconItem.data.url}`
              : fullFaviconUrl;
        }

        let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement("link") as HTMLLinkElement;
          link.rel = "shortcut icon";
          document.head.appendChild(link);
        }
        link.type = "image/x-icon";
        link.href = fullFaviconUrl;
      } catch (error) {
        console.error("Error fetching content:", error);
      }
    };

    fetchContent();
  }, []);

  // if (isLoading) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center">
  //       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  //     </div>
  //   );
  // }
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <CartProvider>
          {/* <NotificationProvider> */}
          <div className="app-container">
            <Routes>
              <Route path={`${base_path}/`} element={
                loadingConfig ? (
                  <div className="d-flex justify-center align-items-center" style={{ height: "100vh" }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : isLandingPage ? (<Landing
                  yourLogo={yourLogo}
                  footerLogo={lightLogo}
                  base_path={base_path}
                  countryId={countryId}
                  countryName={countryName}
                  currencyCode={currencyCode}
                  exposure={exposure}
                />) :
                  <Login base_path={base_path} yourLogo={yourLogo} setCurrencyCode={setCurrencyCode} isExploreEnabled={isExploreEnabled} isLandingPage={isLandingPage} />
              } />
              {/* <Route
                path={`${base_path}/`}
                element={
                  loadingConfig ? (
                    <div className="d-flex justify-center align-items-center" style={{ height: "100vh" }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : isLandingPage ? (
                    <>
                      <Header yourLogo={yourLogo} configData={configData} base_path={base_path} />
                      <main>
                        <Home configData={configData}
                          base_path={base_path}
                          countryId={countryId}
                          countryName={countryName}
                          currencyCode={currencyCode}
                          isExploreEnabled={isExploreEnabled} />
                      </main>
                      <Footer configData={configData} base_path={base_path} yourLogo={yourLogo} />
                    </>
                  ) : (
                    <Login base_path={base_path} yourLogo={yourLogo} setCurrencyCode={setCurrencyCode} isExploreEnabled={isExploreEnabled} isLandingPage={isLandingPage}/>
                  )
                }
              /> */}
              <Route path={`${base_path}/login`} element={
                <ProtectedLoginRoute>
                  <Login base_path={base_path} yourLogo={yourLogo} setCurrencyCode={setCurrencyCode} isExploreEnabled={isExploreEnabled} isLandingPage={isLandingPage}/>
                </ProtectedLoginRoute>
              } />
              <Route path={`${base_path}/sign-up`} element={
                <ProtectedLoginRoute>
                  <Register registrationFields={registrationFields} loading={loadingConfig} yourLogo={yourLogo} />
                </ProtectedLoginRoute>
              } />
              <Route path={`${base_path}/verify-email`} element={
                <ProtectedLoginRoute>
                  <VerifyEmail yourLogo={yourLogo} />
                </ProtectedLoginRoute>
              } />
              <Route path={`${base_path}/forgot-password`} element={
                <ProtectedLoginRoute>
                  <ForgotPassword base_path={base_path} />
                </ProtectedLoginRoute>
              } />
              <Route path={`${base_path}/reset-password`} element={
                <ProtectedLoginRoute>
                  <ResetPassword />
                </ProtectedLoginRoute>
              } />
              <Route path={`${base_path}/explore`} element={
                <ProtectedExploreRoute>
                  <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                    <Explore base_path={base_path}  exposure={exposure} configData={configData}/>
                </DashboardLayout>
                </ProtectedExploreRoute>
              } />
              <Route path="/media-owners" element={<MediaOwners />} />
              {/* <Route path={`${base_path}/billboards/:id`} element={<BillboardDetail />} /> */}
              {/* <Route path="/package/:id" element={<PackageDetail />} /> */}
              <Route path="/deal/:id" element={<DealDetail />} />
              {/* <Route path="/property-details/:id" element={<CampaignBillboardDetail />} /> */}
              <Route path="/store/:ownerId" element={<MediaOwnerStore />} />
              <Route path="/contact/:ownerId" element={<ContactMediaOwner />} />
              <Route path="/contact-media-owner/:ownerId" element={<ContactMediaOwner />} />
              <Route path={`${base_path}/propertylist`} element={<StartCampaign yourLogo={yourLogo} footerLogo={lightLogo} base_path={base_path} configData={configData} currencyCode={currencyCode} exposure={exposure}/>} />
              <Route path={`${base_path}/mycart`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <Cart currencyCode={currencyCode} tax={tax ?? undefined} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/property-details/:id`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <CampaignBillboardDetail base_path={base_path} currencyCode={currencyCode} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/billboard/:id`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <BillboardDetail base_path={base_path} currencyCode={currencyCode} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/my-campaigns`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <Campaigns base_path={base_path} yourLogo={yourLogo} configData={configData} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/account`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <Account />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/contenthub`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <ContentHub base_path={base_path} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/campaigns/:campaignId/proof-of-play`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <ProofOfPlay base_path={base_path} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/delivery-report`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <DeliveryReport base_path={base_path} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/payment-invoice`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <PaymentInvoice base_path={base_path} currencyCode={currencyCode} yourLogo={yourLogo} configData={configData} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/checkout`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <Checkout base_path={base_path} yourLogo={yourLogo} currencyCode={currencyCode} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/creative-management`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <CreativeManagement />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/package/:id`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <PackageDetail base_path={base_path} currencyCode={currencyCode}/>
                </DashboardLayout>
              } />
              {/* <Route path={`${base_path}/propertylist`} element={
                <DashboardLayout yourLogo={yourLogo} configData={configData} base_path={base_path}>
                  <StartCampaign base_path={base_path} configData={configData} currencyCode={currencyCode}/>
                </DashboardLayout>
              } /> */}
              <Route path={`${base_path}/proof-of-logs`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <ProofOfLogs base_path={base_path} />
                </DashboardLayout>
              } />
              <Route path={`${base_path}/daily-report`} element={
                <DashboardLayout yourLogo={yourLogo} footerLogo={lightLogo} configData={configData} base_path={base_path}>
                  <DailyReport base_path={base_path} />
                </DashboardLayout>
              } />
              {/* Add more routes as needed */}
              {/* <Route path="*" element={<Navigate to={`${base_path}/`} replace />} /> */}
              <Route path="/campaign-review" element={<CampaignReview />} />
              <Route element={<NotFound />} />
            </Routes>
            {/* <ToastContainer
              position="top-right"
              autoClose={2500}
              hideProgressBar={false}
              newestOnTop={true}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            /> */}
            <Toaster />
          </div>
          {/* </NotificationProvider> */}
        </CartProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default App;